// Pure combo-group selection math shared by ProductOptionView.jsx. Pulled out
// of the component file (rather than kept as a local function) so it can be
// unit tested directly and so the component file keeps a component-only
// export contract (react-refresh/only-export-components).

/**
 * Stricter-of-two-caps for a multi-select combo position's stepper: the
 * position's own per-item `quantity` field vs. the group's remaining total
 * (max_select minus what sibling positions already use). Returns `Infinity`
 * to mean "uncapped" — NOT 0. A finite 0 is a real cap (the group's total
 * budget is already used up by sibling positions) and must stay
 * distinguishable from "no limit" for the caller to disable the stepper
 * correctly; collapsing both cases to 0 is what let combos exceed max_select
 * (a position with room of its own kept incrementing past the group total
 * because 0 was misread as unlimited).
 *
 * @param {{ maxSelect: number, positions: Array<{ id: string, ownMaxQuantity?: number }> }} group
 * @param {{ id: string, ownMaxQuantity?: number }} position
 * @param {Record<string, { quantity?: number }>} positionState
 */
export function getComboPositionQuantityCap(group, position, positionState) {
  const othersTotal = group.positions.reduce((sum, p) => {
    if (p.id === position.id) return sum;
    return sum + (positionState[p.id]?.quantity ?? 0);
  }, 0);
  const groupRemaining =
    group.maxSelect > 0 ? Math.max(0, group.maxSelect - othersTotal) : Infinity;
  const ownCap = position.ownMaxQuantity > 0 ? position.ownMaxQuantity : Infinity;
  return Math.min(groupRemaining, ownCap);
}

/**
 * Current selection count for a combo group's badge/validation: summed picked
 * quantity for a multi-select group, distinct selected positions for a
 * single-select group.
 *
 * @param {{ isMultiSelect: boolean, positions: Array<{ id: string }> }} group
 * @param {Record<string, { quantity?: number }>} positionState
 */
export function getComboGroupSelectedCount(group, positionState) {
  if (group.isMultiSelect) {
    return group.positions.reduce((sum, p) => sum + (positionState[p.id]?.quantity ?? 0), 0);
  }
  return group.positions.filter((p) => (positionState[p.id]?.quantity ?? 0) > 0).length;
}
